import React, {useEffect, useState} from 'react'

import {appendScript} from '@miro-site/core/src/utils'

interface WistiaHookParams {
  id: string
  onReady: (video?: WistiaApi) => void
  jsonp?: boolean
  options?: {
    fitStrategy?: 'cover' | 'fill' | 'contain' | 'none'
    playlistLoop?: boolean
  }
}

// Duplicating this time for now to avoid having to configure apps to understand aliases/package for types
interface WistiaApi {
  bind: (event: 'play', handler: () => void) => void
  unbind: (event: 'play', handler: () => void) => void
  remove: () => void
  play(): void
  pause(): void
  time(time: number): void
}

// Initialize custom event for wistia
if (typeof window !== 'undefined') {
  window._wq = window._wq || []

  window._wq.push({
    id: '_all',
    onReady: () => {
      document.dispatchEvent(new Event('wistia_loaded'))
    },
  })
}

const classnames = {
  thumbnail: 'wistia_thumbnail',
  responsiveWrapper: 'wistia_responsive_wrapper',
  deferred: 'wistia_deferred',
  embed: 'wistia_embed',
  swatch: 'wistia_swatch',
  background: 'background',
  async: id => `wistia_async_${id}`,
}

const wistiaFlags = {
  noSEO: 'seo=false',
  videoFoam: 'videoFoam=true',
  autoPlay: 'autoPlay=true',
}

// Hook to initialize the listeners/setup on wistia (managing classes to display the video), use useEffect to avoid SSR issues
const useWistia = (videoId?: string): void => {
  const addThumbnailsListeners = () => {
    const thumbnails = [].slice.call(document.getElementsByClassName(classnames.thumbnail))

    for (const thumbnail of thumbnails) {
      if (!thumbnail.dataset.hasListeners) {
        thumbnail.dataset.hasListeners = true

        thumbnail.addEventListener('click', handleThumbnailClick)
        thumbnail.addEventListener('keypress', handleThumbnailKeyPress)
      }
    }
  }

  const handleThumbnailKeyPress = event => {
    event.preventDefault()

    if (event.key === ' ' || event.key === 'Enter') {
      handleThumbnailClick(event)
    }
  }

  const handleThumbnailClick = event => {
    const thumbnail = event.target
    const wrapper = thumbnail.parentNode.parentNode
    const deferred = wrapper.querySelector(`.${classnames.deferred}`)

    if (deferred) {
      deferred.classList.add(classnames.responsiveWrapper)
      wrapper.style.minHeight = `${thumbnail.offsetHeight}px`
      thumbnail.classList.add(classnames.background)

      initWistiaVideo({wrapper: deferred})
    }
  }

  const initializeWrappers = () => {
    const wrappers = [].slice.call(document.getElementsByClassName(classnames.responsiveWrapper))

    if (wrappers.length === 0) {
      return
    }

    for (const wrapper of wrappers) {
      initWistiaVideo({wrapper})
    }
  }

  useEffect(() => {
    if (videoId) {
      addThumbnailsListeners()
      initializeWrappers()
    }
  })
}

const initWistiaVideo = ({
  wrapper,
  embedId = null,
  autoPlay = false,
  videoId = null,
  classes = [],
}: {
  wrapper: HTMLElement
  embedId?: string
  autoPlay?: boolean
  videoId?: string
  classes?: string[]
}): void => {
  if (!wrapper || wrapper.dataset.wistiaPrepared) {
    return
  }

  const wistiaId = wrapper.dataset.wistiaVideoId || videoId

  if (wistiaId) {
    appendScript('https://fast.wistia.com/assets/external/E-v1.js')

    wrapper.dataset.wistiaPrepared = 'true'

    const wrapperIsDeferred = wrapper.classList.contains(classnames.deferred)
    const embedClasses = [
      classnames.embed,
      classnames.async(wistiaId),
      wistiaFlags.noSEO,
      wistiaFlags.videoFoam,
    ]

    if (classes.length) {
      embedClasses.push(...classes)
    }

    if (autoPlay || wrapperIsDeferred) {
      embedClasses.push(wistiaFlags.autoPlay)
    }

    const embed = document.createElement('div')
    embed.classList.add(...embedClasses)

    const swatch = document.createElement('div')
    swatch.classList.add(classnames.swatch)
    swatch.style.cssText =
      'height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;'

    wrapper.appendChild(embed).appendChild(swatch)

    wrapper.dataset.testid = 'wistia-element-ref'

    if (embedId) {
      embed.id = embedId
    }
  }
}

// Wistia hook so we can have easy access to "onReady" listener and get the video api
const useWistiaVideoApi = ({id, jsonp, onReady, options = {}}: WistiaHookParams): WistiaApi => {
  const [videoApi, setVideoApi] = useState(null)

  useEffect(() => {
    window._wq = window._wq || []
    window._wq.push({
      id,
      onReady: (api): WistiaApi => {
        setVideoApi(api)

        if (jsonp) {
          appendScript(`https://fast.wistia.com/embed/medias/${id}.jsonp`)
        }

        onReady && onReady(api)

        return api
      },
      options,
    })
  }, [])

  return videoApi
}

export {initWistiaVideo, useWistia, useWistiaVideoApi}
